/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { devtools } from 'zustand/middleware';
import { create } from 'zustand';

import {
  fetchPublicConfiguration,
  type PublicConfiguration,
} from './public-config';

/**
 * Plugin configuration from backend
 */
export interface PluginConfiguration {
  coze_saas_plugin_enabled?: boolean;
  coze_api_token?: string;
  coze_saas_api_base_url?: string;
  show_register_button?: boolean;
  enable_create_app?: boolean;
}

export interface ServiceConfiguration {
  enable_single_agent_mode?: boolean;
  enable_better_harness_mode?: boolean;
  /** @deprecated Read-only compatibility for persisted pre-rename config. */
  enable_multi_agent_mode?: boolean;
  enable_workflow_agent_mode?: boolean;
  enable_client_side_tool_agent_mode?: boolean;
  enable_skill?: boolean;
  enable_image_generation_surface?: boolean;
  enable_agui_internal_web?: boolean;
  /** @deprecated Use enable_agui_internal_web. */
  enable_agui_preview?: boolean;
  enable_a2ui_preview?: boolean;
}

export interface BrandConfiguration {
  product_name?: string;
  login_welcome_message?: string;
  logo_url?: string;
  favicon_url?: string;
}

/**
 * Basic configuration from backend
 */
export interface BasicConfiguration {
  evaluation_observability_configuration?: {enable_adp_loop?: boolean};
  default_locale?: PublicConfiguration['default_locale'];
  admin_emails?: string;
  disable_user_registration?: boolean;
  allow_registration_email?: string;
  plugin_configuration?: PluginConfiguration;
  server_host?: string;
  service_configuration?: ServiceConfiguration;
  brand_configuration?: BrandConfiguration;
}

export interface SystemConfigStoreState {
  publicConfig: PublicConfiguration | null;
  initialized: boolean;
  loading: boolean;
  error: string | null;
  config: BasicConfiguration | null;
}

export interface SystemConfigStoreActions {
  fetchPublicConfig: () => Promise<PublicConfiguration>;
  fetchConfig: () => Promise<void>;
  setConfig: (config: BasicConfiguration) => void;
}

const DEFAULT_STATE: SystemConfigStoreState = {
  publicConfig: null,
  initialized: false,
  loading: false,
  error: null,
  config: null,
};

/**
 * Fetch basic configuration from backend API
 */
const fetchBasicConfig = async (): Promise<BasicConfiguration> => {
  let authenticatedConfig: BasicConfiguration | undefined;
  try {
    const response = await fetch('/api/admin/config/basic/get');
    if (response.ok) {
      const data = await response.json();
      authenticatedConfig = data.configuration || {};
    }
  } catch (error) {
    console.error('Failed to fetch authenticated config:', error);
  }

  try {
    const pubResponse = await fetch('/api/system/public_config');
    if (pubResponse.ok) {
      const pubData = await pubResponse.json();
      return {
        ...authenticatedConfig,
        brand_configuration:
          authenticatedConfig?.brand_configuration ??
          pubData.brand_configuration,
        evaluation_observability_configuration: {enable_adp_loop: pubData.adp_loop_enabled === true},
        service_configuration: {
          ...authenticatedConfig?.service_configuration,
          enable_better_harness_mode:
            pubData.better_harness_enabled ??
            pubData.super_agent_enabled ??
            false,
          enable_image_generation_surface:
            pubData.image_generation_surface_enabled ??
            authenticatedConfig?.service_configuration
              ?.enable_image_generation_surface ??
            true,
          enable_agui_internal_web:
            pubData.agui_internal_web_enabled ??
            pubData.agui_preview_enabled ??
            false,
          enable_agui_preview:
            pubData.agui_internal_web_enabled ??
            pubData.agui_preview_enabled ??
            false,
          enable_a2ui_preview: pubData.a2ui_preview_enabled ?? false,
          enable_client_side_tool_agent_mode:
            pubData.client_side_tool_agent_mode_enabled ??
            authenticatedConfig?.service_configuration
              ?.enable_client_side_tool_agent_mode ??
            false,
          enable_skill:
            pubData.skill_enabled ??
            pubData.better_harness_enabled ??
            pubData.super_agent_enabled ??
            false,
        },
      };
    }
  } catch (error) {
    console.error('Failed to fetch public config:', error);
  }

  if (authenticatedConfig) {
    return {
      ...authenticatedConfig,
      evaluation_observability_configuration: {enable_adp_loop: false},
      service_configuration: {
        ...authenticatedConfig.service_configuration,
        enable_better_harness_mode: false,
        enable_multi_agent_mode: false,
        enable_agui_internal_web: false,
        enable_agui_preview: false,
        enable_a2ui_preview: false,
      },
    };
  }
  throw new Error('Failed to fetch system config');
};

let publicConfigRequest: Promise<PublicConfiguration> | undefined;

export const useSystemConfigStore = create<
  SystemConfigStoreState & SystemConfigStoreActions
>()(
  devtools(
    (set, get) => ({
      ...DEFAULT_STATE,

      fetchPublicConfig: () => {
        const cached = get().publicConfig;
        if (cached) {
          return Promise.resolve(cached);
        }
        publicConfigRequest ??= fetchPublicConfiguration().then(config => {
          set({ publicConfig: config });
          publicConfigRequest = undefined;
          return config;
        });
        return publicConfigRequest;
      },

      fetchConfig: async () => {
        // Avoid duplicate fetching
        if (get().initialized || get().loading) {
          return;
        }

        set({ loading: true, error: null });

        try {
          const [config, publicConfig] = await Promise.all([
            fetchBasicConfig(),
            get().fetchPublicConfig(),
          ]);
          set({
            config: { ...config, default_locale: publicConfig.default_locale },
            initialized: true,
            loading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Unknown error',
            loading: false,
            initialized: true,
          });
        }
      },

      setConfig: (config: BasicConfiguration) => {
        set({ config, initialized: true });
      },
    }),
    { name: 'system-config-store' },
  ),
);

/**
 * Hook to get system configuration with auto-fetch
 */
export const useSystemConfig = () => {
  const { config, publicConfig, initialized, loading, fetchConfig } =
    useSystemConfigStore();

  // Auto-fetch on first use
  if (!initialized && !loading) {
    fetchConfig();
  }

  return {
    config,
    initialized,
    loading,
    agentStoreAllowBothUI: publicConfig?.agent_store_allow_both_ui === true,
    agentStoreFrontendVersion:
      publicConfig?.agent_store_frontend_version ?? 'v2',
    defaultLocale: publicConfig?.default_locale ?? 'en-US',
    enableADPLoop: config?.evaluation_observability_configuration?.enable_adp_loop === true,
    // Helper getters with defaults
    showRegisterButton:
      config?.plugin_configuration?.show_register_button ?? true,
    enableCreateApp: config?.plugin_configuration?.enable_create_app ?? false,
    enableCozePluginStore:
      config?.plugin_configuration?.coze_saas_plugin_enabled ?? false,
    enableSingleAgentMode:
      config?.service_configuration?.enable_single_agent_mode ?? true,
    enableBetterHarnessMode:
      config?.service_configuration?.enable_better_harness_mode ??
      config?.service_configuration?.enable_multi_agent_mode ??
      false,
    enableWorkflowAgentMode:
      config?.service_configuration?.enable_workflow_agent_mode ?? true,
    enableClientSideToolAgentMode:
      config?.service_configuration?.enable_client_side_tool_agent_mode ??
      false,
    enableSkill: config?.service_configuration?.enable_skill ?? false,
    enableImageGenerationSurface:
      config?.service_configuration?.enable_image_generation_surface ?? true,
    enableAGUIPreview:
      config?.service_configuration?.enable_agui_internal_web ??
      config?.service_configuration?.enable_agui_preview ??
      false,
    enableA2UIPreview:
      config?.service_configuration?.enable_a2ui_preview ?? false,
    brandConfiguration: config?.brand_configuration,
  };
};
